<template>
  <div>
    <div class="main py-4">
      <discount-zone-pricing-modal 
        v-if="isZonePricingModal" 
        :zone="this.discountZone" 
        :accountId="accountId"
        :accountName="userInfo.name"
        :allowActions="true" 
        @zonesUpdated="handleZonesUpdated"
        @closeModal="closeDiscountZoneModal">
      </discount-zone-pricing-modal>
      <!-- Account Info -->
      <div class="sub-container mx-4">
        
        <div class="mt-2">
          <label class="flex items-center space-x-2">
            <regular-checkbox :id="'vehicle-ready'" :checked="userInfo.applyVehicleSurplus" :value="userInfo.applyVehicleSurplus" @change="updateStatus()"
              :label="$t('Account.billing.apply_vehicle_surplus')" />
          </label>
        </div>

        <div>
          <label class="flex items-center space-x-2">
            <regular-checkbox :id="'vehicle-ready'" :checked="userInfo.applyFuelCharge" :value="userInfo.applyFuelCharge" @change="updateFuelStatus()"
              :label="$t('Account.billing.apply_fuel_charge')" class="mb-2" />
          </label>
        </div>

        <div class="mt-2">
          <label for="fuelCharge">{{ $t('Account.billing.fuel_charge_percent') }}</label>
          <regular-input
            id="fuelCharge"
            type="number"
            min="0"
            :value="userInfo.fuelCharge"
            @input="userInfo.fuelCharge = $event"
            :placeholder="$t('Account.billing.fuel_charge_percent_placeholder')" />
        </div>

        <div class="mt-2">
          <label for="odooCustomer">{{ $t('Account.billing.odoo_customer') }}</label>
          <model-select
            id="odooCustomer"
            class="mt-1"
            :options="odooCustomerOptions"
            :value="String(userInfo.odooPartnerId || '')"
            @input="updateOdooPartner"
            :isDisabled="isOdooCustomersLoading"
            :placeholder="$t('Account.billing.odoo_customer_placeholder')" />
          <small v-if="isOdooCustomersLoading" class="text-muted d-block mt-1">
            {{ $t('Account.billing.odoo_customer_loading') }}
          </small>
          <small v-else-if="selectedOdooCustomerText" class="text-muted d-block mt-1">
            {{ $t('Account.billing.odoo_customer_selected') }}: {{ selectedOdooCustomerText }}
          </small>
          <small v-else-if="odooCustomers.length === 0" class="text-muted d-block mt-1">
            {{ $t('Account.billing.odoo_customer_no_results') }}
          </small>
        </div>

        <div class="mt-2">
          <label class="flex items-center space-x-2">
            <regular-checkbox :id="'zero-tax'" :checked="userInfo.zeroTax" @change="updateZeroTax()"
              :label="$t('Account.billing.zero_tax')" class="mb-2" />
          </label>
        </div>

        <div class="mt-2">
          <label class="flex items-center space-x-2">
            <regular-checkbox
              :id="'allow-different-logistics-email'"
              :checked="userInfo.allowDifferentLogisticsEmail === true"
              @change="updateAllowDifferentLogisticsEmail"
              :label="$t('Account.billing.allow_different_logistics_email')"
              class="mb-2"
            />
          </label>
        </div>

        <div class="mt-2">
          <label for="billingComment">{{ $t('Account.billing.billing_comment') }}</label>
          <textarea
            id="billingComment"
            class="form-control"
            rows="3"
            :value="userInfo.billingComment"
            @input="userInfo.billingComment = $event.target.value"
            :placeholder="$t('Account.billing.billing_comment_placeholder')"
          ></textarea>
        </div>

        <div class="mt-2">
          <!-- Add Location Price Button -->
          <button 
            v-if="!showLocationPriceModal"
            class="btn btn-primary mb-3" 
            @click="openLocationPriceModal"
          >
            {{ $t('Account.billing.add_location_price') }}
          </button>

          <!-- Location Price Modal -->
          <div v-if="showLocationPriceModal" class="border px-2 py-1">
            <div class="bg-white rounded shadow-lg">
              <h4 class="mb-2">{{ $t('Account.billing.new_location_price') }}</h4>
              
              <!-- Pickup Location Autocomplete -->
              <div class="form-group mb-2">
                <label class="mt-2" for="pickupLocation">{{ $t('Account.billing.choose_pick_address') }}</label>
                <vue-google-autocomplete
                  class="form-control border"
                  id="pickupLocation"
                  ref="pickupAutocomplete"
                  :placeholder="$t('Account.billing.pick_address')"
                  v-on:placechanged="getPickupAddressData"
                  :country="['ca', 'us']"
                  :fields="autocompleteSearchFields">
                </vue-google-autocomplete>
              </div>

              <!-- Dropoff Location Autocomplete -->
              <div class="form-group mb-3">
                <label class="mt-2" for="dropoffLocation">{{ $t('Account.billing.choose_drop_address') }}</label>
                <vue-google-autocomplete
                  class="form-control border"
                  id="dropoffLocation"
                  ref="dropoffAutocomplete"
                  :placeholder="$t('Account.billing.drop_address')"
                  v-on:placechanged="getDropoffAddressData"
                  :country="['ca', 'us']"
                  :fields="autocompleteSearchFields">
                </vue-google-autocomplete>
              </div>

              <!-- Price Input -->
              <div class="form-group mb-3">
                <label>{{ $t('Price ($)') }}</label>
                <regular-input 
                  type="number"
                  v-model="newLocationPrice.price"
                  :placeholder="$t('Account.billing.enter_price')"
                />
              </div>

              <div class="flex justify-between">
                <button 
                  class="btn btn-primary" 
                  @click="saveLocationPrice"
                >
                  {{ $t('Account.billing.save') }}
                </button>
                <button 
                  class="btn btn-secondary" 
                  @click="closeLocationPriceModal"
                >
                  {{ $t('Account.billing.cancel') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div class="mt-2">
          <regular-input 
              style="flex: 2;" 
              type="text" 
              :value="userInfo.billingComment" 
              @input="userInfo.billingComment = $event" 
              :placeholder="$t('Account.billing.billingComment')" />
        </div>

        <div>
          <label class="mt-2" for="fax">{{ $t('Account.billing.discountTypes') }}</label>
          <regular-select
            class="mb-3"
            :value="this.userInfo.discountType"
            @change="selectDiscountType"
            :placeholder="$t('Account.billing.select_discount_type')">
            <option v-for="discount in discountTypes" :key="discount.label" :value="discount.value">{{ discount.label }}</option>
          </regular-select>
        </div>

        <div v-if="this.isPriceBasedDiscount">
          <div style="display: flex;" class="m-b-8 pr-ctr">
            <button 
              class="w-btn n-b-r" 
              :class="{ 'selected': discountCategory === '+' }"
              @click="selectDiscountCategory('+')">+</button>
            <button 
              class="w-btn" 
              :class="{ 'selected': discountCategory === '-' }"
              @click="selectDiscountCategory('-')">-</button>
            <regular-input 
              style="flex: 2;" 
              min="0" 
              type="number" 
              :value="discountAmount" 
              @input="discountAmount = $event" 
              :placeholder="$t('Account.billing.discount_amount')" />
            <button 
              class="w-btn n-b-r" 
              :class="{ 'selected': discountAmountType === '$' }"
              @click="selectDiscountAmountType('$')">$</button>
            <button 
              class="w-btn" 
              :class="{ 'selected': discountAmountType === '%' }"
              @click="selectDiscountAmountType('%')">%</button>
          </div>
          <div v-if="isIncompletePriceDiscount" class="my-2">
            <p class="text-danger">{{  $t('Account.billing.incomplete_price_discount_data') }}</p>
          </div>
          <div v-if="!!discountAmount && isPriceBasedDiscount">
            <button class="btn btn-blk btn-cnl btn-primary" @click="confirmDiscount">{{ $t('Account.billing.confirm_discount') }}</button>
          </div>
        </div>

        <div v-if="isZoneBasedDiscount" class="">
          <regular-select
            :placeholder="$t('Zones.select_zone')"
            style="flex: 2"
            @change="selectDiscountZone"
            class="border form-group"
            v-model="selectedZone">
            <option v-for="(zon, index) in this.zones" :key="index" :value="zon.id">
              {{ zon.zoneName }}
            </option>
          </regular-select>
        </div>

        <div class="" v-if="!!selectedZone && isZoneBasedDiscount">
          <button class="btn btn-blk btn-cnl btn-primary" @click="configureZoneDiscountPrice">{{ $t('Account.billing.configure_discount') }}</button>
        </div>

        <div class="mt-4 controllers">
          <button class="btn btn-kne-orange w-50" @click="saveBilling">{{ $t('Account.billing.save_discount') }}</button>
          <button class="btn btn-danger w-50 ml-2" @click="onCloseBillingModal">{{ $t('Account.billing.cancel_discount') }}</button>
        </div>

      </div>
      <div v-if="isPriceBasedDiscount" class="sub-container mx-4">
        <p class="font-weight-bold mb-1">{{ $t('Account.billing.current_discount_rate') }}</p>
        <p class="" v-if="userInfo.discountAmount">{{ this.userInfo.discountCategory ? this.userInfo.discountCategory : '' }} {{ '' + this.userInfo.discountAmount + '' + this.userInfo.discountAmountType + ' ' + $t('Account.billing.discount_per_order') }}</p>
        <p v-if="!userInfo.discountAmount">{{ $t('Account.billing.no_price_discount') }}</p>
      </div>
      <div v-if="isZoneBasedDiscount" class="sub-container mx-4">
        <p class="font-weight-bold mb-1">{{ $t('Account.billing.discounted_zones') }}</p>
        <div class="" v-for="zon in this.userInfo.discountedZones">
          {{ userInfo.zones[zon].zoneName }}
        </div>
        <p v-if="this.userInfo.discountedZones.length === 0">{{ $t('Account.billing.no_zone_discount') }}</p>
      </div>
      <div class="mt-2">
        <h4><b>{{ $t('Account.billing.location_prices')}}</b></h4>
        <div v-if="userInfo.locationPrices && userInfo.locationPrices.length" class="mt-3">
    <div 
      v-for="(locPrice, index) in userInfo.locationPrices" 
      :key="index" 
      class="flex justify-between items-center border-b my-2"
    >
      <span>
        <b>{{ $t('Account.billing.pickup')}}: </b>{{ locPrice.pickupLocation }} 
        <br>
        <b>{{ $t('Account.billing.drop')}}: </b>{{ locPrice.dropoffLocation }}
        <br>
        <b>{{ $t('Account.billing.price')}}: </b>
        <template v-if="editingPriceIndex !== index">
          <b>${{ locPrice.price }}</b>
        </template>
        <template v-else>
          <input 
            type="number" 
            v-model.number="editingPrice" 
            class="form-control inline-input w-20 mb-2"
            @keyup.enter="saveEditedPrice(index, $event)"
            @blur="saveEditedPrice(index, $event)"
          />
        </template>
      </span>
      <div>
        <button 
          v-if="editingPriceIndex !== index"
          class="btn btn-sm btn-success mr-2" 
          @click="startEditPrice(index, locPrice.price)"
        >
          <i class="fas fa-edit"></i>
        </button>
        <button 
          v-if="editingPriceIndex === index"
          class="btn btn-sm btn-success mr-2" 
          @click.stop="saveEditedPrice(index, $event)"
        >
          <i class="fas fa-save"></i>
        </button>
        <button 
          class="btn btn-sm btn-danger" 
          @click="deleteLocationPrice(index)"
        >
          <i class="fas fa-trash-alt"></i>
        </button>
      </div>
    </div>
  </div>
        <p v-else class="text-muted">
          {{ $t('Account.billing.no_location_prices') }}
        </p>
      </div>

    </div>
  </div>
</template>
  
<script>
  import regularInput from '../../basic-components/regularInput.vue'
  import regularSelect from '../../basic-components/regularSelect.vue'
  import regularCheckbox from '../../basic-components/regularCheckbox.vue'
  import Navbar from '../../interface-components/NavBar.vue'
  // import CustomGoogleAutocomplete from '../Custom-packages/Custom-GoogleAutocomplete.vue'
  import VueGoogleAutocomplete from 'vue-google-autocomplete'
  import { ModelSelect } from 'vue-search-select'
  import hoverToolTip from '../../basic-components/hoverToolTip.vue'
  import DiscountZonePricingModal from '../Modal-templates/DiscountZonePricing-modal.vue'
  
  import 'vue-search-select/dist/VueSearchSelect.css'
  import { db } from '../../../../firebase.js'
  
  export default {
    name: 'BillingDiscountForm',
  
    props: {
      edittedAccount: {
        type: Object,
        required: false
      },
  
      /** Show certain buttons in edit mode */
      isEditMode: {
        type: Boolean,
        required: true
      },
  
      transporterList: {
        type: [Array, Boolean, String],
        required: false
      },
  
      newStatus: {
        type: String,
        required: true
      },
  
      companyUID: {
        type: String,
        required: false
      }
    },
  
    components: {
      regularInput,
      regularSelect,
      regularCheckbox,
      Navbar,
      DiscountZonePricingModal,
      VueGoogleAutocomplete,
      ModelSelect,
      hoverToolTip
    },
  
    data () {
      return {
        userInfo: {
          name: '',
          address: '',
          city: '',
          postcode: '',
          province: '',
          billingComment: '',
          allowDifferentLogisticsEmail: false,
          contacts: [],
          paymentMethod: '',
          creditApplication: '',
          discountType: '',
          discountAmount: '',
          discountAmountType: '',
          discountCategory: '',
          fuelCharge: '',
          odooPartnerId: '',
          odooPartnerName: '',
          odooPartnerEmail: '',
          odooPartnerRef: '',
          odooPartnerCompanyId: '',
          odooPartnerCompanyName: '',
          zeroTax: false,
          zones: {},
          discountedZones: [],
          country: '',
          phone: '',
          fax: '',
          email: '',
          password: '',
          sageID: '',
          transporterUid: '',
          isActive: true
        },
        showLocationPriceModal: false,
      newLocationPrice: {
        pickupLocation: '',
        dropoffLocation: '',
        pickupPlaceData: null,
        dropoffPlaceData: null,
        price: null
      },
      editingPriceIndex: null,
      editingPrice: null,
      editingLocationPriceIndex: null,
      autocompleteSearchFields: [
        'address_components',
        'adr_address',
        'formatted_address',
        'geometry',
        'id',
        'name',
        'place_id',
        'type',
        'vicinity'
      ],
        discountTypes: [
          {value: 'Price_Based_Discount', label: `${this.$t('Account.billing.price_based_discount')}`},
          {value: 'Zone_Based_Discount', label: `${this.$t('Account.billing.zone_based_discount')}`}
        ],
        isIncompletePriceDiscount: false,
        discountAmount: '',
        discountAmountType: '',
        discountCategory: '',
        isUserInfoFilled: { // This serves as setting required fields and changing missing fields style
          // name: false,
          address: false,
          city: false,
          postcode: false,
          province: false,
          country: false
          // phone: false,
          // email: false,
          // password: false
        },
        isGoogleInfoEmpty: {
          address: true,
          city: true,
          postcode: true,
          province: true,
          country: true
        },
        selectedZone: '',
        selectedZoneIdx: '',
        discountZone: {},
        zones: [],
        odooCustomers: [],
        isOdooCustomersLoading: false,
        transpLinkList: [],
        memoryTransptLinkList: {},
        isZonePricingModal: false,
        accountId: '',
        errorMessage: '',
        isLoading: false,
        status: {
          approved: 'Approved',
          pending: 'Pending',
          denied: 'Denied',
          edit: 'Edit'
        }
      }
    },
  
    created () {
      this.userInfoIsEdit()
      this.accountId = this.edittedAccount['.key']
      this.initializeUser()
      this.loadOdooCustomers()
      this.setTransporterListFromParent(this.companyUID)
      this.getZones()
    },
  
    methods: {
      /** Approve account */
      onApproveAccount () {
        if (!this.initiateFormProcess()) return
  
        this.isLoading = true
        const EAKey = this.edittedAccount['.key']
  
        const newAccountDetails = this.setEdittedFields()
  
        this.saveDealerAccountDetails(newAccountDetails, EAKey)
        this.sendEmail(this.userInfo.email)
  
        /** @emits TransporterAccounts.vue */
        // this.$emit('closeEditModal', false)
      },

      updateStatus () {
        this.userInfo.applyVehicleSurplus = !this.userInfo.applyVehicleSurplus
      },

      updateFuelStatus () {
        this.userInfo.applyFuelCharge = !this.userInfo.applyFuelCharge
      },

      updateZeroTax () {
        this.userInfo.zeroTax = !this.userInfo.zeroTax
      },

      updateAllowDifferentLogisticsEmail (value) {
        this.$set(this.userInfo, 'allowDifferentLogisticsEmail', value === true)
      },

      normalizeOdooPartnerId (value) {
        const id = Number(value)
        return Number.isInteger(id) && id > 0 ? id : ''
      },

      buildOdooCustomerText (customer) {
        const name = customer.displayName || customer.name || `#${customer.odooId || customer.value}`
        const details = []
        if (customer.companyName) details.push(customer.companyName)
        if (customer.email) details.push(customer.email)
        if (customer.ref) details.push(customer.ref)
        return details.length ? `${name} - ${details.join(' - ')}` : name
      },

      buildOdooCustomerSearchText (customer) {
        return [
          customer.name,
          customer.displayName,
          customer.email,
          customer.phone,
          customer.city,
          customer.ref,
          customer.companyName,
          customer.companyId,
          customer.odooId
        ].filter(Boolean).join(' ').toLowerCase()
      },

      normalizeOdooCustomer (customer, key) {
        const odooId = this.normalizeOdooPartnerId(customer.odooId || customer.id || key)
        const normalized = {
          value: odooId ? String(odooId) : '',
          odooId,
          name: customer.name || '',
          displayName: customer.displayName || customer.display_name || '',
          email: customer.email || '',
          phone: customer.phone || '',
          city: customer.city || '',
          ref: customer.ref || '',
          companyId: customer.companyId || '',
          companyName: customer.companyName || ''
        }
        normalized.text = this.buildOdooCustomerText(normalized)
        normalized.searchText = this.buildOdooCustomerSearchText(normalized)
        return normalized
      },

      async loadOdooCustomers () {
        this.isOdooCustomersLoading = true
        try {
          const snapshot = await db.ref('odoo/partners/customers').once('value')
          const customers = []
          snapshot.forEach((child) => {
            const customer = this.normalizeOdooCustomer(child.val() || {}, child.key)
            if (customer.value) customers.push(customer)
          })
          customers.sort((a, b) => a.text.localeCompare(b.text))
          this.odooCustomers = customers
        } catch (error) {
          console.error('Error loading Odoo customers:', error)
        } finally {
          this.isOdooCustomersLoading = false
        }
      },

      updateOdooPartner (value) {
        const odooPartnerId = this.normalizeOdooPartnerId(value)
        const selectedCustomer = this.odooCustomers.find(customer => String(customer.value) === String(odooPartnerId))
        this.$set(this.userInfo, 'odooPartnerId', odooPartnerId)
        this.$set(this.userInfo, 'odooPartnerName', selectedCustomer ? selectedCustomer.name || selectedCustomer.displayName || '' : '')
        this.$set(this.userInfo, 'odooPartnerEmail', selectedCustomer ? selectedCustomer.email || '' : '')
        this.$set(this.userInfo, 'odooPartnerRef', selectedCustomer ? selectedCustomer.ref || '' : '')
        this.$set(this.userInfo, 'odooPartnerCompanyId', selectedCustomer ? selectedCustomer.companyId || '' : '')
        this.$set(this.userInfo, 'odooPartnerCompanyName', selectedCustomer ? selectedCustomer.companyName || '' : '')
      },

      openLocationPriceModal () {
      this.showLocationPriceModal = true
      // Reset autocomplete inputs when modal opens
      if (this.$refs.pickupAutocomplete) {
        this.$refs.pickupAutocomplete.clear()
      }
      if (this.$refs.dropoffAutocomplete) {
        this.$refs.dropoffAutocomplete.clear()
      }
    },

    closeLocationPriceModal () {
      this.showLocationPriceModal = false
      this.editingLocationPriceIndex = null
      this.newLocationPrice = {
        pickupLocation: '',
        dropoffLocation: '',
        pickupPlaceData: null,
        dropoffPlaceData: null,
        price: null
      }
    },

    getPickupAddressData (addressData, placeResultData) {
      this.newLocationPrice.pickupLocation = placeResultData.name || placeResultData.formatted_address.split(',')[0] || ''
      this.newLocationPrice.pickupPlaceData = {
        placeId: placeResultData.place_id,
        lat: placeResultData.geometry.location.lat(),
        lng: placeResultData.geometry.location.lng()
      }
    },

    getDropoffAddressData (addressData, placeResultData) {
      this.newLocationPrice.dropoffLocation = placeResultData.name || placeResultData.formatted_address.split(',')[0] || ''
      this.newLocationPrice.dropoffPlaceData = {
        placeId: placeResultData.place_id,
        lat: placeResultData.geometry.location.lat(),
        lng: placeResultData.geometry.location.lng()
      }
    },

    saveLocationPrice () {
      // Validate input
      if (!this.newLocationPrice.pickupLocation ||
          !this.newLocationPrice.dropoffLocation ||
          !this.newLocationPrice.price) {
        alert(this.$t('Account.billing.fill_all_fields'))
        return
      }

      // Initialize location prices array if not exists
      if (!this.userInfo.locationPrices) {
        this.$set(this.userInfo, 'locationPrices', [])
      }

      // Edit or add new location price
      const locationPriceToSave = {
        pickupLocation: this.newLocationPrice.pickupLocation,
        dropoffLocation: this.newLocationPrice.dropoffLocation,
        price: this.newLocationPrice.price,
        pickupPlaceData: this.newLocationPrice.pickupPlaceData,
        dropoffPlaceData: this.newLocationPrice.dropoffPlaceData
      }

      if (this.editingLocationPriceIndex !== null) {
        this.userInfo.locationPrices[this.editingLocationPriceIndex] = locationPriceToSave
        this.editingLocationPriceIndex = null
      } else {
        this.userInfo.locationPrices.push(locationPriceToSave)
      }
      // Close modal and reset
      this.closeLocationPriceModal()
    },

    startEditPrice (index, currentPrice) {
      this.editingPriceIndex = index
      this.editingPrice = currentPrice
    },

    saveEditedPrice (index) {
      // Validate price
      if (this.editingPrice !== null && this.editingPrice !== '' && this.editingPrice > 0) {
        this.$set(this.userInfo.locationPrices[index], 'price', this.editingPrice)

        // Reset editing state
        this.editingPriceIndex = null
        this.editingPrice = null
      } else {
        alert(this.$t('Account.billing.invalid_price'))
      }
    },

    deleteLocationPrice (index) {
      this.userInfo.locationPrices.splice(index, 1)
    },

      /** Set the edit object @param updatedFields to be passed into @function onApproveAccount() */
      setEdittedFields (status) {
        const uInfo = this.userInfo
        let liste = this.setTransporterLinkList()
  
        let updatedFields = {
          'name': uInfo.name,
          'address': uInfo.address,
          'city': uInfo.city,
          'postcode': uInfo.postcode,
          'province': uInfo.province,
          'contacts': uInfo.contacts || [],
          'paymentMethod': uInfo.paymentMethod || '',
          'billingComment': uInfo.billingComment || '',
          'creditAmount': uInfo.creditAmount || '',
          'creditApplication': uInfo.creditApplication || '',
          'discountType': uInfo.discountType || '',
          'discountAmountType': uInfo.discountAmountType || '',
          'applyVehicleSurplus': uInfo.applyVehicleSurplus === undefined ? true : uInfo.applyVehicleSurplus,
          'applyFuelCharge': uInfo.applyFuelCharge === undefined ? true : uInfo.applyFuelCharge,
          'fuelCharge': uInfo.fuelCharge === undefined ? '' : uInfo.fuelCharge,
          'odooPartnerId': this.normalizeOdooPartnerId(uInfo.odooPartnerId),
          'odooPartnerName': uInfo.odooPartnerName || '',
          'odooPartnerEmail': uInfo.odooPartnerEmail || '',
          'odooPartnerRef': uInfo.odooPartnerRef || '',
          'odooPartnerCompanyId': uInfo.odooPartnerCompanyId || '',
          'odooPartnerCompanyName': uInfo.odooPartnerCompanyName || '',
          'zeroTax': uInfo.zeroTax === true,
          'allowDifferentLogisticsEmail': uInfo.allowDifferentLogisticsEmail === true,
          'discountAmount': uInfo.discountAmount || '',
          'discountCategory': uInfo.discountCategory || '',
          'discountedZones': uInfo.discountedZones || [],
          'zones': uInfo.zones || {},
          'country': uInfo.country,
          'phone': uInfo.phone,
          'fax': uInfo.fax,
          'sageID': uInfo.sageID,
          'liste': liste,
          'companyUID': this.companyUID
        }
  
        return updatedFields
      },
  
      /** Approved accounts have their @param companyUid
       * set to the logged in account that approved them
      */
      setTransporterLinkList () {
        if (!this.userInfo.liste) this.userInfo.liste = {}
        let transporterList = this.userInfo.liste
  
        this.transpLinkList.forEach(transporter => {
          let tempObject = {}
          let uid = ''
  
          uid = transporter.uid
  
          tempObject = {
            [uid]: transporter.text
          }
  
          transporterList = Object.assign(this.userInfo.liste, tempObject)
        })
  
        return transporterList
      },

      handleZonesUpdated ({ zoneId, updatedZones }) {
        this.$set(this.userInfo.zones, zoneId, {
            ...this.userInfo.zones[zoneId],
            ...updatedZones
        })
      },

      saveBilling () {
        try {
          db.ref('users/dealers/' + this.accountId).set(
            this.userInfo
          ).catch(function (error) {
            console.error(error)
          })
        } catch (err) {
          console.error(err)
        }
        this.onCloseBillingModal()
      },

      onCloseBillingModal () {
        this.$emit('closeEditModal')
      },
  
      async initializeUser () {
        try {
          let snapshot = await db.ref(`users/dealers/${this.accountId}`).once('value')
          if (snapshot.exists()) {
            this.userInfo = snapshot.val()
            if (!this.userInfo.discountType) {
              this.$set(this.userInfo, 'discountedType', '')
            }
            if (!this.userInfo.discountedZones) {
              this.$set(this.userInfo, 'discountedZones', [])
            }
            if (!('applyVehicleSurplus' in this.userInfo)) {
              this.$set(this.userInfo, 'applyVehicleSurplus', true)
            }
            if (!('applyFuelCharge' in this.userInfo)) {
              this.$set(this.userInfo, 'applyFuelCharge', true)
            }
            if (!('fuelCharge' in this.userInfo)) {
              this.$set(this.userInfo, 'fuelCharge', '')
            }
            if (!('odooPartnerId' in this.userInfo)) {
              this.$set(this.userInfo, 'odooPartnerId', '')
            }
            if (!('odooPartnerName' in this.userInfo)) {
              this.$set(this.userInfo, 'odooPartnerName', '')
            }
            if (!('odooPartnerEmail' in this.userInfo)) {
              this.$set(this.userInfo, 'odooPartnerEmail', '')
            }
            if (!('odooPartnerRef' in this.userInfo)) {
              this.$set(this.userInfo, 'odooPartnerRef', '')
            }
            if (!('odooPartnerCompanyId' in this.userInfo)) {
              this.$set(this.userInfo, 'odooPartnerCompanyId', '')
            }
            if (!('odooPartnerCompanyName' in this.userInfo)) {
              this.$set(this.userInfo, 'odooPartnerCompanyName', '')
            }
            if (!('zeroTax' in this.userInfo)) {
              this.$set(this.userInfo, 'zeroTax', false)
            }
            if (!('allowDifferentLogisticsEmail' in this.userInfo)) {
              this.$set(this.userInfo, 'allowDifferentLogisticsEmail', false)
            }
          }
        } catch (err) {
          console.error(err)
        }
      },

      closeDiscountZoneModal () {
        this.isZonePricingModal = false
      },

      async getZones () {
        try {
          if (!this.$store.state.zonesLoaded) {
            await this.$store.dispatch('subscribeToZones')
          }
          const storeZones = this.$store.state.zones
          const companyZoneIds = storeZones.map(z => z.id)

          let snapshot = await db.ref(`users/dealers/${this.accountId}/zones/`).once('value')
          if (snapshot.exists()) {
            this.userInfo.zones = snapshot.val()
            this.userInfo.zones = Object.keys(this.userInfo.zones)
              .filter(key => companyZoneIds.includes(key))
              .reduce((obj, key) => {
                  obj[key] = this.userInfo.zones[key]
                  return obj
              }, {})
            this.userInfo.discountedZones = this.userInfo.discountedZones
              .filter(zone => companyZoneIds.includes(zone))
          } else {
            if (storeZones.length) {
              this.userInfo.zones = storeZones.reduce((obj, zone) => {
                obj[zone.id] = zone
                return obj
              }, {})
              this.onApproveAccount()
            } else {
              this.userInfo.zones = {}
            }
          }
          this.zones = Object.entries(this.userInfo.zones).map(([id, zoneData]) => ({
            id,
            ...zoneData
          }))
        } catch (error) {
          console.error('Error fetching zones:', error)
        }
      },

      setUserInfo () {
        this.userInfo = this.$store.state.userInfo
      },

      selectDiscountType (event) {
        this.$set(this.userInfo, 'discountType', event)
      },

      selectDiscountZone (event) {
        this.selectedZone = event
        const zone = this.zones.filter(zon => zon.id === event)
        this.discountZone = zone[0]
      },

      selectDiscountAmountType (operation) {
        this.discountAmountType = operation
      },

      selectDiscountCategory (operation) {
        this.discountCategory = operation
      },

      confirmDiscount () {
        if (!this.discountAmount || !this.discountAmountType || !this.discountCategory) {
          this.isIncompletePriceDiscount = true
        } else {
          this.isIncompletePriceDiscount = false
          this.userInfo.discountAmount = this.discountAmount
          this.userInfo.discountAmountType = this.discountAmountType
          this.userInfo.discountCategory = this.discountCategory
        }
      },

      configureZoneDiscountPrice () {
        if (!this.userInfo.discountedZones.includes(this.selectedZone)) {
          this.userInfo.discountedZones.push(this.selectedZone)
        }
        this.isZonePricingModal = true
      },

      closeDicountZoneModal () {
        this.getZones()
        this.isZonePricingModal = false
      },

      /** Performs basic checks before continuing the form process */
      initiateFormProcess () {
        this.errorMessage = ''
        let isGoodToGo
  
        let areAllFieldsFilled = this.checkFilledInfo()
        if (areAllFieldsFilled) isGoodToGo = true
        else isGoodToGo = false
  
        return isGoodToGo
      },
  
      /** Update at @param EAKey new info */
      saveDealerAccountDetails (newAccountDetails, EAKey) {
         db.ref('users').child('dealers').child(EAKey).update(newAccountDetails)
      },
  
      updateAccountInfoFB (EAKey, updatedAccountInfo) {
        db.ref('users').child('pending-creation').child(EAKey).update(updatedAccountInfo)
      },
  
      /** Send an email to the created account that they are pogging */
      sendEmail (emailTarget) {
        let emailObj = {
          to: emailTarget,
          subject: 'Account Created',
          text: 'Hi, <br><br> Your account is now ready to use.<br><br>Thank you,<br>Keep N Eye'
        }
  
        db.ref('email-request4').push().set(emailObj)
      },
  
      /** Pass through each key attribute in @param userInfo and check if its value is empty
       *  If it's empty, update @param areAllFieldsFilled to false
       */
      checkFilledInfo () {
        let areAllFieldsFilled = true
  
        for (const key in this.isUserInfoFilled) {
          if (Object.hasOwnProperty.call(this.isUserInfoFilled, key)) {
            const element = this.userInfo[key]
            if (element === '') {
              this.isUserInfoFilled[key] = true // For input fields that are missing
              areAllFieldsFilled = false
              this.setErrorMessage('missing-fields')
            } else this.isUserInfoFilled[key] = false
          }
        }
  
        return areAllFieldsFilled
      },
  
      /** Some error types are given by Firebase's account auth system */
      setErrorMessage (errorType) {
        switch (errorType) {
          case 'auth/invalid-email':
            this.errorMessage = this.$t('Account.modal.email_error')
            this.isUserInfoFilled.email = true
            break
  
          case 'auth/weak-password':
            this.errorMessage = this.$t('Account.modal.password_error')
            this.isUserInfoFilled.password = true
            break
  
          case 'missing-fields':
            this.errorMessage = this.$t('Account.modal.missing_fields_error')
  
            break
          default:
            this.errorMessage = this.$t('Account.modal.default_error')
            break
        }
      },
  
      /** When edit modal is opened set @param edittedAccount as @param userInfo */
      userInfoIsEdit () {
        for (const key in this.edittedAccount) {
          if (Object.hasOwnProperty.call(this.edittedAccount, key)) {
            this.userInfo[key] = this.edittedAccount[key]
          }
        }

        this.setBackupTranspLinkList()
      },
  
      /** Save a backup of the transporter link list of the editted account.
       * Needed because some problems from closing the modal while a transporter is removed
       * (But you can't actually remove a transporter in this version lul)
       */
      setBackupTranspLinkList () {
        if (!this.userInfo.liste) return
  
        for (const key in this.userInfo.liste) {
          if (Object.hasOwnProperty.call(this.userInfo.liste, key)) {
            this.memoryTransptLinkList[key] = this.userInfo.liste[key]
          }
        }
      },

      setTransporterListFromParent (companyUID) {
        this.transporterList.find((o) => {
          if (companyUID === o['.key']) {
            this.transpLinkList = [{
              'text': o.name,
              'uid': o['.key'],
              'value': 1
            }]
          }
        })
      }
    },

    computed: {
      odooCustomerOptions () {
        return this.odooCustomers.map(customer => ({
          value: String(customer.value),
          text: customer.text
        }))
      },

      selectedOdooCustomerText () {
        const selectedValue = String(this.userInfo.odooPartnerId || '')
        if (!selectedValue) return ''

        const selectedCustomer = this.odooCustomers.find(customer => String(customer.value) === selectedValue)
        if (selectedCustomer) return selectedCustomer.text

        if (this.userInfo.odooPartnerName) return `${this.userInfo.odooPartnerName} (#${selectedValue})`
        return `#${selectedValue}`
      },

      getTransporterLinks () {
        const tempListe = this.userInfo.liste
  
        if (!tempListe) {
          let errorMessage = []
          return errorMessage
        }
  
        return tempListe
      },

      isLocationBasedDiscount () {
        return this.userInfo.discountType === 'Location_Based_Discount'
      },

      transpSelectOptions () {
        if (!this.transporterList) return
        let transpArray = []
  
        this.transporterList.forEach((transporter, i) => {
          transpArray.push({
            value: i,
            text: transporter.name,
            uid: transporter['.key']
          })
        })
  
        return transpArray
      },

      isPriceBasedDiscount () {
        return this.userInfo.discountType === 'Price_Based_Discount'
      },

      isZoneBasedDiscount () {
        return this.userInfo.discountType === 'Zone_Based_Discount'
      }
    }
  }
</script>
  
  <style scoped>
    .main {
      display: flex;
      justify-content: center;
      flex-flow: row;
    }
  
    .tm-ctr .w-btn {
      width: 35px !important;
      font-size: 20px;
      border: 1px solid gray;
      outline: none;
      }

    .sub-container {
      min-width: 25%;
      max-width: 25%;
    }
  
    .button-container {
      display: flex;
      justify-content: space-between;
    }
  
    img {
      min-width: 200px;
      max-width: 50%;
      height: auto;
    }
  
    .red-text {
      color: rgb(220, 0, 0);
    }
  
    .red {
      border-radius: 3px;
      border: 1px solid red !important;
    }
    label {
      white-space: nowrap;
    }
  
    .link_removed-opacity {
      opacity: 0.5;
    }

    .pr-d {
      display: flex;
      justify-content: space-between;
      row-gap: 5px;
    }

    .m-b-8 {
      margin-bottom: 8px;
    }
    .pr-ctr input:disabled {
      background-color: #fff;
    }
    .w-btn {
      width: 30px;
      background-color: white;
      border: 1px solid #ddd;
    }
    .w-btn:focus {
      outline: none;
    }
    .w-btn.selected {
      background-color: #007bff;
      color: white;
    }
    .n-b-r {
      border-right: none !important;
    }
    .controllers {
      display: flex;
      justify-content: space-between;
      row-gap: 5px;
    }
</style>
